import { createSelector } from '@reduxjs/toolkit';

import type Item from '../@types/item';
import type {
  Commerce,
  ExpectedDelivery,
  InventoryStatusString,
  OdometerReading,
} from '../@types/item';
import type { RootState } from '../reducers';
import addCustomProperties from '../utils/vehicle-properties/custom-property-adder';
import adjustProperties from '../utils/vehicle-properties/property-adjust';
import enrichWithHint from '../utils/vehicle-properties/property-hint-enricher';
import removeProperties from '../utils/vehicle-properties/property-remover';
import PropertyTreeCreator from '../utils/vehicle-properties/property-tree-creator';
import sortTree from '../utils/vehicle-properties/property-tree-sorter';

export const getItem = createSelector(
  (state: RootState) => state.item.current,
  (documentList): Item | null =>
    !!documentList && !!documentList.documents && documentList.documents.length === 1
      ? documentList.documents[0]
      : null,
);

export const getItemCommerce = createSelector([getItem], (item): Commerce | null =>
  item ? item.commerce : null,
);

interface GetItemInformation {
  status: string;
  title: string;
  shortDescription?: string | null;
  description: string;
  fuelTypes: string;
  gearboxType: string;
  mileage: number;
  modelYear: number;
  isDemoVersion: boolean;
  odometerReading: OdometerReading;
  registrationNumber: string;
  inventoryStatusString?: InventoryStatusString;
  availableFrom?: string;
  expectedDelivery?: ExpectedDelivery;
}

export const getItemInformation = createSelector([getItem], (item): GetItemInformation | null => {
  if (!item) return null;

  return {
    status: item.status,
    title: item.title,
    shortDescription: item.shortDescription,
    description: item.description,
    fuelTypes: item.fuelTypes,
    gearboxType: item.gearboxType,
    mileage: item.mileage,
    modelYear: item.modelYear,
    isDemoVersion: item.isDemoVersion,
    odometerReading: item.odometerReading,
    registrationNumber: item.registrationNumber,
    inventoryStatusString: item.inventoryStatusString,
    availableFrom: item.availableFrom,
    expectedDelivery: item.expectedDelivery,
  };
});

interface GetItemContact {
  price: number;
}

export const getItemContact = createSelector([getItem], (item): GetItemContact | null => {
  if (!item) return null;
  return {
    price: item.price,
  };
});

interface GetItemPrice {
  price: number;
  oldPrice: number;
  leasingPrice: number;
  deductibleVat: boolean;
  businessLeasingPrice: number;
  leasingDeposit?: number;
}

export const getItemPrice = createSelector([getItem], (item): GetItemPrice | null => {
  if (!item) return null;

  return {
    price: item.price,
    oldPrice: item.oldPrice,
    leasingPrice: item.leasingPrice,
    deductibleVat: item.deductibleVat,
    businessLeasingPrice: item.businessLeasingPrice,
    leasingDeposit: item.leasing?.deposit,
  };
});

export const getPrivateLeasingPromo = createSelector([getItem], (item) =>
  item?.leasingPromos?.find((x) => !x.isBusinessLeasing),
);

export const getBusinessLeasingPromo = createSelector([getItem], (item) =>
  item?.leasingPromos?.find((x) => x.isBusinessLeasing),
);

export const getItemOptions = createSelector([getItem], (item) => {
  if (!item) return null;

  return item.options || [];
});

export const getItemId = createSelector([getItem], (item) => (item ? item.id : null));

export const getItemBranch = createSelector([getItem], (item) =>
  item && item.branches && item.branches.length > 0 ? item.branches[0] : null,
);

export const getItemCentralStorageBranches = createSelector([getItem], (item) =>
  item &&
  item.branches &&
  item.branches.length &&
  item.branches[0].connectedBranches &&
  item.branches[0].connectedBranches.length
    ? item.branches[0].connectedBranches
    : null,
);

export const itemIsCentralStorage = createSelector([getItemCentralStorageBranches], (s) => !!s);

export const itemIsAuctionItem = createSelector([getItem], (item) => item && item.isAuctionItem);

export const getItemAuctionUrl = createSelector([getItem], (item) =>
  item ? item.externalUrl : null,
);

const MOLLER_BIL_BRANCHES = [
  'a274a702-0875-42e6-9c6e-95fac29b1342',
  '3cbf82c4-fffe-42cb-9bf3-3f6a407a2fa4',
  '42b01b73-096d-429e-b116-334801c7d58c',
  'c8c512f4-ec03-4279-a060-ea2e9e81b2be',
  '78ba8a45-536b-49eb-851f-73f80cc7e14c',
  '966c9b5c-9c81-48f5-98a4-cd5fe4f3330e',
  '4e5e0e8d-05f6-4df9-884d-3418c3711887',
  '42a3078b-9edd-437b-b939-d6a664de0b8e',
  'a2b303e4-29a2-44ba-8453-4aa611fec545',
  '0c439b93-1976-4529-86dc-ecb99df44782',
  '115d537d-6f8b-4309-9d6c-fb21fe9b1200',
  '190c5a2c-53b9-4038-9f65-965d976442cb',
  '9da4761c-8eb5-41c1-8651-39a704382ab0',
  '41fd21dc-98ff-41c7-ada7-19144be442b2',
  'b515b2c2-6908-42b0-a4d5-a2ced631d48f',
  'be60e74d-71a2-4dc9-ab2e-5da1e363753b',
  'dd297c0d-2b53-43df-a647-5743a8650f2b',
  '12c27759-ae28-4cb0-bbbe-b54a6a9e83f9',
  '82922cfa-3480-4989-8f87-30404fa3259e',
  '839ebd2a-a374-40ec-be14-e3f92defa65b',
  '7cc0dcd0-9f53-4694-808a-3e1652d28023',
  'ed10a26e-f933-4422-bb46-ac3e55ef55a5',
  'c28700b4-3712-49d1-90f7-d8b080b63a1d',
  'ab44f1f3-e762-40e3-b155-5b3f0299933e',
  '830442ad-33ef-413f-85aa-e31a34db1b34',
  '0ed59cc1-6f74-4a93-a9a0-da0880c03613',
  '3d27f368-ff2a-442c-b7da-8e9921945953',
  '5a96cc00-a5b2-47cd-aafa-dae9278aad21',
  '7a1556ca-8d99-483f-9975-2efbbbdc27a3',
  '7c170011-1a87-4166-bde4-889ad7613462',
  '8a4d9cfd-480d-43ab-86a9-732e31643cb5',
  '8a5b3167-448d-4b30-bcac-c423b4bbb392',
  '8ecdbc31-26e0-4a0d-a6f7-fd5a28d9e3d2',
  '8fbb2e93-5ea9-4628-9c16-d22465eb5925',
  '9aefabde-3013-441a-bed3-68d34e156eff',
  '9c215bf9-43a8-4989-ae62-ae931e019178',
  'a51eee72-725d-416a-9a75-e3bc5b4c0229',
  'adfed7a5-8206-4221-a11f-82a7eede872c',
  'e3b4b2f6-c761-4bab-bebc-7c43ed0616bc',
  'f46eb5fa-da36-4be2-8439-6790b5786a35',
  'fc3739ad-e5c5-47fb-bf40-cb1678794b28',
];

export const getPropertyTree = createSelector(getItem, (item) => {
  const properties = item?.enhancedProperties || {};
  const branchIds = item?.branches?.map((x) => x.id);
  const isMollerBil = !!branchIds?.some((x) => MOLLER_BIL_BRANCHES.includes(x));

  let newProperties = removeProperties(properties, isMollerBil);
  newProperties = addCustomProperties(item, newProperties);
  const adjustedProperties = adjustProperties(newProperties);
  const enrichedProperties = enrichWithHint(adjustedProperties);
  const tree = new PropertyTreeCreator(enrichedProperties).create();

  sortTree(tree);
  return tree;
});
